import React, { useState, useEffect } from "react";
import {
  FaReceipt,
  FaPercentage,
  FaPlus,
  FaEdit,
  FaTrash,
  FaCheckCircle,
  FaTimesCircle,
  FaCalculator,
  FaBuilding,
  FaTimes,
  FaSearch,
  FaUndo,
  FaStar,
  FaRegStar,
  FaEllipsisV,
  FaEye,
} from "react-icons/fa";
import { toast } from "react-toastify";
import apiEndpoints from "../../apiendpoints.json";
import { FaSyncAlt } from "react-icons/fa";
import { getAuthHeaders, handleAuthError } from "../../utils/auth";

const BASE_URL = apiEndpoints.equityUrl || "http://localhost:5217";

// Predefined Indian States for GST state-matching logic
const INDIAN_STATES = [
  "Uttar Pradesh",
  "Maharashtra",
  "Delhi",
  "Karnataka",
  "Tamil Nadu",
  "Gujarat",
  "Rajasthan",
  "West Bengal",
  "Telangana",
  "Madhya Pradesh",
  "Haryana",
  "Bihar",
  "Punjab",
  "Andhra Pradesh",
  "Kerala",
  "Odisha",
  "Assam",
  "Jharkhand",
  "Chhattisgarh",
  "Uttarakhand",
  "Goa",
  "Himachal Pradesh",
  "Tripura",
  "Meghalaya",
  "Manipur",
  "Nagaland",
  "Sikkim",
  "Arunachal Pradesh",
  "Mizoram",
  "Jammu & Kashmir",
  "Chandigarh",
  "Puducherry",
  "Ladakh",
];

// Initial static tax and charges data
const INITIAL_CHARGES = [
  {
    id: 1,
    name: "Standard Goods & Services Tax (GST)",
    code: "GST_18",
    category: "GST",
    type: "Percentage",
    rate: 18.0,
    cgstRate: 9.0,
    sgstRate: 9.0,
    igstRate: 18.0,
    scope: "Standalone (Global)",
    applicableOn: "All Services & Packages",
    description: "Standard GST slab for digital financial advisory and equity research services.",
    isActive: true,
    isDefault: true,
  },
  {
    id: 2,
    name: "Concessional / Advisory GST",
    code: "GST_12",
    category: "GST",
    type: "Percentage",
    rate: 12.0,
    cgstRate: 6.0,
    sgstRate: 6.0,
    igstRate: 12.0,
    scope: "Service-Specific",
    applicableOn: "Selected Educational Publications",
    description: "Applicable on eligible research journals, print publications & literature.",
    isActive: true,
    isDefault: false,
  },
  {
    id: 3,
    name: "Reduced Research & Training GST",
    code: "GST_5",
    category: "GST",
    type: "Percentage",
    rate: 5.0,
    cgstRate: 2.5,
    sgstRate: 2.5,
    igstRate: 5.0,
    scope: "Service-Specific",
    applicableOn: "Investor Education & Training Workshops",
    description: "Concessional rate for certified public investor awareness & education programs.",
    isActive: true,
    isDefault: false,
  },
  {
    id: 4,
    name: "Luxury / High Advisory GST",
    code: "GST_28",
    category: "GST",
    type: "Percentage",
    rate: 28.0,
    cgstRate: 14.0,
    sgstRate: 14.0,
    igstRate: 28.0,
    scope: "Service-Specific",
    applicableOn: "Bespoke Family Office Retainers",
    description: "High bracket statutory slab for high-tier institutional advisory retainers.",
    isActive: false,
    isDefault: false,
  },
  {
    id: 5,
    name: "Zero Rated Export Advisory GST",
    code: "GST_0",
    category: "GST",
    type: "Percentage",
    rate: 0.0,
    cgstRate: 0.0,
    sgstRate: 0.0,
    igstRate: 0.0,
    scope: "Service-Specific",
    applicableOn: "Cross-border FPI & Overseas Clients",
    description: "Zero-rated tax supply for eligible overseas research report subscriptions.",
    isActive: false,
    isDefault: false,
  },
  {
    id: 6,
    name: "Platform Convenience Fee",
    code: "PLATFORM_FEE",
    category: "Other Charge",
    type: "Flat",
    rate: 20.0,
    cgstRate: null,
    sgstRate: null,
    igstRate: null,
    scope: "Standalone (Global)",
    applicableOn: "All Checkout Transactions",
    description: "Flat platform infrastructure and gateway facilitation surcharge per invoice.",
    isActive: true,
  },
  {
    id: 7,
    name: "Portfolio Onboarding / Audit Fee",
    code: "PORTFOLIO_AUDIT",
    category: "Other Charge",
    type: "Flat",
    rate: 499.0,
    cgstRate: null,
    sgstRate: null,
    igstRate: null,
    scope: "Service-Specific",
    applicableOn: "HNI Portfolio Review",
    description: "One-time onboarding and detailed portfolio assessment fee.",
    isActive: true,
  },
  {
    id: 8,
    name: "Express Delivery Surcharge",
    code: "EXPRESS_SURCHARGE",
    category: "Other Charge",
    type: "Percentage",
    rate: 2.5,
    cgstRate: null,
    sgstRate: null,
    igstRate: null,
    scope: "Service-Specific",
    applicableOn: "Priority High Frequency Signals",
    description: "Optional priority distribution line charge for instant push alerts.",
    isActive: true,
  },
  {
    id: 9,
    name: "Securities Transaction Tax (STT)",
    code: "STT_CHARGE",
    category: "Other Charge",
    type: "Percentage",
    rate: 0.1,
    cgstRate: null,
    sgstRate: null,
    igstRate: null,
    scope: "Service-Specific",
    applicableOn: "Equity Delivery Facilitation",
    description: "Direct statutory tax levied on value of securities transacted.",
    isActive: true,
  },
  {
    id: 10,
    name: "SEBI Regulatory Turnover Fee",
    code: "SEBI_TURNOVER",
    category: "Other Charge",
    type: "Percentage",
    rate: 0.001,
    cgstRate: null,
    sgstRate: null,
    igstRate: null,
    scope: "Service-Specific",
    applicableOn: "Exchange Orders",
    description: "Regulatory fee mandated by SEBI per crore of traded volume.",
    isActive: true,
  },
  {
    id: 11,
    name: "Depository Participant (DP) Charge",
    code: "DP_CHARGE",
    category: "Other Charge",
    type: "Flat",
    rate: 15.5,
    cgstRate: null,
    sgstRate: null,
    igstRate: null,
    scope: "Service-Specific",
    applicableOn: "Demat Debit Transactions",
    description: "Fixed depository transaction and maintenance clearing fee.",
    isActive: true,
  },
  {
    id: 12,
    name: "Instant SMS & WhatsApp Advisory Alert Fee",
    code: "SMS_ALERT_FEE",
    category: "Other Charge",
    type: "Flat",
    rate: 50.0,
    cgstRate: null,
    sgstRate: null,
    igstRate: null,
    scope: "Service-Specific",
    applicableOn: "VIP Mobile Signal Subscribers",
    description: "Monthly telecom gateway facilitation fee for live market calls.",
    isActive: false,
  },
];

export const ENTITY_NAME = "equity";

const TaxManagementPanel = () => {
  const [currentEntityId, setCurrentEntityId] = useState(2);
  const [isLoading, setIsLoading] = useState(false);
  const [charges, setCharges] = useState(INITIAL_CHARGES);

  const fetchTaxes = async (entityIdentifier = ENTITY_NAME) => {
    setIsLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/api/invoices/taxes?entity=${entityIdentifier}`, {
        headers: getAuthHeaders(),
      });
      if (res.status === 401) {
        handleAuthError();
        return;
      }
      const data = await res.json();
      if (data.success && data.payload && data.payload.length > 0) {
        const mapped = data.payload.map((t) => {
          const rateVal = parseFloat(t.chargeValue) || 0;
          const isGst = (t.chargeCode && t.chargeCode.toUpperCase().includes("GST")) ||
                        (t.chargeName && t.chargeName.toUpperCase().includes("GST"));
          return {
            id: t.id,
            name: t.chargeName,
            code: t.chargeCode,
            category: isGst ? "GST" : "Other Charge",
            type: t.chargeType || "Percentage",
            rate: rateVal,
            cgstRate: isGst ? rateVal / 2 : null,
            sgstRate: isGst ? rateVal / 2 : null,
            igstRate: isGst ? rateVal : null,
            scope: t.isStandalone ? "Standalone (Global)" : "Service-Specific",
            applicableOn: t.applicableOn || "All Services & Packages",
            description: t.description || "",
            isActive: t.isActive,
            isDefault: t.isDefault,
          };
        });
        setCharges(mapped);
        const def = mapped.find((m) => m.isDefault);
        if (def) setSelectedGstId(def.id);
      }
    } catch (err) {
      console.warn("Could not load taxes from backend:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTaxes(currentEntityId);
  }, [currentEntityId]);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");

  // Three-dot dropdown menu & details modal state
  const [openMenuId, setOpenMenuId] = useState(null);
  const [viewingCharge, setViewingCharge] = useState(null);

  // Modal State for Add / Edit Charge
  const [showModal, setShowModal] = useState(false);
  const [editingCharge, setEditingCharge] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    category: "GST",
    type: "Percentage",
    rate: "",
    scope: "Standalone (Global)",
    applicableOn: "All Services & Packages",
    description: "",
    isActive: true,
  });

  // Simulator / Calculator State
  const [calcBaseAmount, setCalcBaseAmount] = useState(10000);
  const [businessState, setBusinessState] = useState("Uttar Pradesh");
  const [customerState, setCustomerState] = useState("Uttar Pradesh");
  const [includePlatformFee, setIncludePlatformFee] = useState(true);
  const [includeOtherChargesUnderGst, setIncludeOtherChargesUnderGst] = useState(false);
  const [selectedGstId, setSelectedGstId] = useState(1);

  // Active filters
  const filteredCharges = charges.filter((item) => {
    const matchesSearch =
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.applicableOn.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      categoryFilter === "All" || item.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const displayedCharges = filteredCharges;

  // Metrics
  const totalCharges = charges.length;
  const activeGstSlabs = charges.filter((c) => c.category === "GST" && c.isActive).length;
  const activeOtherCharges = charges.filter((c) => c.category !== "GST" && c.isActive).length;

  // Toggle Active Status
  const handleToggleStatus = async (id) => {
    try {
      const res = await fetch(`${BASE_URL}/api/invoices/taxes/${id}/toggle-status`, {
        method: "PATCH",
        headers: getAuthHeaders(),
      });
      if (res.status === 401) {
        handleAuthError();
        return;
      }
    } catch (e) {
      console.warn("Status toggle fallback");
    }
    setCharges((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, isActive: !item.isActive } : item
      )
    );
    toast.success("Charge status updated!");
  };

  // Delete Charge
  const handleDeleteCharge = async (id) => {
    if (window.confirm("Are you sure you want to remove this tax/charge configuration?")) {
      try {
        const res = await fetch(`${BASE_URL}/api/invoices/taxes/${id}`, {
          method: "DELETE",
          headers: getAuthHeaders(),
        });
        if (res.status === 401) {
          handleAuthError();
          return;
        }
      } catch (e) {
        console.warn("Delete fallback");
      }
      setCharges((prev) => prev.filter((item) => item.id !== id));
      toast.success("Charge removed successfully!");
    }
  };

  // Set a single GST Rate as Default (unsets any previous default)
  const handleSetDefaultGst = async (id) => {
    try {
      const res = await fetch(`${BASE_URL}/api/invoices/taxes/${id}/default`, {
        method: "PATCH",
        headers: getAuthHeaders(),
      });
      if (res.status === 401) {
        handleAuthError();
        return;
      }
    } catch (e) {
      console.warn("Set default fallback");
    }
    setCharges((prev) =>
      prev.map((item) => {
        if (item.category === "GST") {
          return {
            ...item,
            isDefault: item.id === id,
            isActive: item.id === id ? true : item.isActive,
          };
        }
        return item;
      })
    );
    setSelectedGstId(id);
    const targetGst = charges.find((c) => c.id === id);
    toast.success(`"${targetGst?.name || "Selected GST"}" set as default GST rate!`);
  };

  // Open Create Modal
  const handleOpenCreate = () => {
    setEditingCharge(null);
    setFormData({
      name: "",
      code: "",
      category: "GST",
      type: "Percentage",
      rate: "",
      scope: "Standalone (Global)",
      applicableOn: "All Services & Packages",
      description: "",
      isActive: true,
      isDefault: false,
    });
    setShowModal(true);
  };

  // Open Edit Modal
  const handleOpenEdit = (charge) => {
    setEditingCharge(charge);
    setFormData({
      name: charge.name,
      code: charge.code,
      category: charge.category,
      type: charge.type,
      rate: charge.rate,
      scope: charge.scope,
      applicableOn: charge.applicableOn,
      description: charge.description,
      isActive: charge.isActive,
      isDefault: !!charge.isDefault,
    });
    setShowModal(true);
  };

  // Submit Modal
  const handleSaveCharge = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.code || formData.rate === "") {
      toast.error("Please fill all required fields.");
      return;
    }

    const rateVal = parseFloat(formData.rate) || 0;
    const isGst = formData.category === "GST";
    const wantsDefault = isGst && formData.isDefault;

    const payload = {
      entityId: currentEntityId,
      chargeName: formData.name.trim(),
      chargeCode: formData.code.trim().toUpperCase(),
      chargeType: formData.type,
      chargeValue: rateVal,
      description: formData.description || "",
      applicableOn: formData.applicableOn || "All Services & Packages",
      isStandalone: formData.scope === "Standalone (Global)",
      isDefault: wantsDefault,
      isActive: wantsDefault ? true : formData.isActive,
    };

    try {
      if (editingCharge) {
        const res = await fetch(`${BASE_URL}/api/invoices/taxes/${editingCharge.id}`, {
          method: "PUT",
          headers: getAuthHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify(payload),
        });
        if (res.status === 401) {
          handleAuthError();
          return;
        }
        toast.success("Tax/Charge updated successfully!");
      } else {
        const res = await fetch(`${BASE_URL}/api/invoices/taxes`, {
          method: "POST",
          headers: getAuthHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify(payload),
        });
        if (res.status === 401) {
          handleAuthError();
          return;
        }
        toast.success("New Tax/Charge added successfully!");
      }
      fetchTaxes(currentEntityId);
    } catch (err) {
      console.error("Save charge error:", err);
      toast.info("Saved locally.");
    }
    setShowModal(false);
  };

  // GST Calculation Logic
  const isIntraState = businessState === customerState;
  const currentGstObj = charges.find((c) => c.id === selectedGstId) || charges[0];
  const gstRate = currentGstObj && currentGstObj.isActive ? currentGstObj.rate : 0;

  const baseAmount = parseFloat(calcBaseAmount) || 0;

  // Active Platform Fee calculation
  const platformFeeObj = charges.find((c) => c.code === "PLATFORM_FEE" && c.isActive);
  const platformFeeAmount =
    includePlatformFee && platformFeeObj ? platformFeeObj.rate : 0;

  // If includeOtherChargesUnderGst is checked, GST applies on (Base Amount + Platform Fee)
  const taxableAmount = includeOtherChargesUnderGst
    ? baseAmount + platformFeeAmount
    : baseAmount;

  let cgstAmount = 0;
  let sgstAmount = 0;
  let igstAmount = 0;
  let totalTax = 0;

  if (isIntraState) {
    cgstAmount = (taxableAmount * (gstRate / 2)) / 100;
    sgstAmount = (taxableAmount * (gstRate / 2)) / 100;
    totalTax = cgstAmount + sgstAmount;
  } else {
    igstAmount = (taxableAmount * gstRate) / 100;
    totalTax = igstAmount;
  }

  const grandTotal = baseAmount + platformFeeAmount + totalTax;

  return (
    <div className="p-3 sm:p-6 md:p-8 bg-gray-50 min-h-screen pb-36 sm:pb-48">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
              <FaReceipt className="text-blue-600 text-lg sm:text-xl shrink-0" />
              <span>Tax & Charges Management</span>
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              Configure GST rates (CGST, SGST, IGST), platform convenience fees, and test real-time tax breakdowns.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            <button
              type="button"
              onClick={() => fetchTaxes(ENTITY_NAME)}
              className="p-2 bg-white hover:bg-gray-50 text-gray-700 rounded-lg border border-gray-300 shadow-xs cursor-pointer"
              title="Refresh Taxes"
            >
              <FaSyncAlt className={`text-xs ${isLoading ? "animate-spin" : ""}`} />
            </button>

            <button
              type="button"
              onClick={handleOpenCreate}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm font-semibold rounded-lg shadow-sm transition-all duration-150 active:scale-95 cursor-pointer"
            >
              <FaPlus className="text-xs" />
              <span>Add New Charge / Slab</span>
            </button>
          </div>
        </div>

        {/* Metric Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <div className="bg-white p-4 sm:p-5 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-wider text-gray-400">
                  Total Configured
                </p>
                <p className="text-xl sm:text-2xl font-bold text-gray-800 mt-1">{totalCharges}</p>
                <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5">Taxes & Surcharges</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                <FaReceipt size={18} />
              </div>
            </div>
          </div>

          <div className="bg-white p-4 sm:p-5 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-wider text-gray-400">
                  Active GST Slabs
                </p>
                <p className="text-xl sm:text-2xl font-bold text-emerald-600 mt-1">{activeGstSlabs}</p>
                <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5">CGST, SGST & IGST Ready</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                <FaPercentage size={18} />
              </div>
            </div>
          </div>

          <div className="bg-white p-4 sm:p-5 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-wider text-gray-400">
                  Active Other Charges
                </p>
                <p className="text-xl sm:text-2xl font-bold text-indigo-600 mt-1">{activeOtherCharges}</p>
                <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5">Platform & Audit Fees</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                <FaBuilding size={18} />
              </div>
            </div>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* INTERACTIVE GST & SURCHARGE CALCULATOR SIMULATOR */}
        {/* ========================================================================= */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 pb-4 border-b border-gray-100 mb-5">
            <div className="flex items-center gap-2">
              <FaCalculator className="text-indigo-600 text-base sm:text-lg shrink-0" />
              <h2 className="text-base sm:text-lg font-bold text-gray-800">
                Live Tax Calculation Simulator
              </h2>
            </div>
            <span className="self-start sm:self-auto text-[11px] sm:text-xs px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 font-semibold border border-blue-200">
              {isIntraState ? "Intra-State (Same State)" : "Inter-State (Different State)"}
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Calculator Inputs */}
            <div className="lg:col-span-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">
                  Base Service / Package Amount (₹)
                </label>
                <input
                  type="number"
                  min="0"
                  value={calcBaseAmount}
                  onChange={(e) => setCalcBaseAmount(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. 10000"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">
                    Seller / Company State
                  </label>
                  <select
                    value={businessState}
                    onChange={(e) => setBusinessState(e.target.value)}
                    className="w-full px-3.5 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    {INDIAN_STATES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">
                    Customer / Buyer State
                  </label>
                  <select
                    value={customerState}
                    onChange={(e) => setCustomerState(e.target.value)}
                    className="w-full px-3.5 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    {INDIAN_STATES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">
                    Applied GST Slab
                  </label>
                  <select
                    value={selectedGstId}
                    onChange={(e) => setSelectedGstId(Number(e.target.value))}
                    className="w-full px-3.5 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    {charges
                      .filter((c) => c.category === "GST")
                      .map((gst) => (
                        <option key={gst.id} value={gst.id}>
                          {gst.name} ({gst.rate}%){gst.isDefault ? " ★ Default" : ""}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="flex flex-col justify-end space-y-2 pb-1">
                  <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700">
                    <input
                      type="checkbox"
                      checked={includePlatformFee}
                      onChange={(e) => setIncludePlatformFee(e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span>Include Platform Fee (₹{platformFeeObj?.rate || 0})</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700">
                    <input
                      type="checkbox"
                      checked={includeOtherChargesUnderGst}
                      onChange={(e) => setIncludeOtherChargesUnderGst(e.target.checked)}
                      className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                    />
                    <span className="flex items-center gap-1">
                      Include other charges under GST
                      <span className="text-[10px] text-gray-400 font-normal">
                        ({includeOtherChargesUnderGst ? "Taxable = Base + Charges" : "Taxable = Base only"})
                      </span>
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Live Calculation Output Card */}
            <div className="lg:col-span-6 bg-slate-900 text-white p-5 rounded-xl flex flex-col justify-between shadow-md">
              <div>
                <div className="flex items-center justify-between pb-3 border-b border-slate-700 gap-2">
                  <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
                    Tax Invoice Breakdown
                  </span>
                  <span
                    className={`inline-flex items-center justify-center text-center text-xs px-2.5 py-1 rounded font-bold leading-none shrink-0 ${
                      isIntraState
                        ? "bg-blue-500/20 text-blue-300 border border-blue-500/30"
                        : "bg-indigo-500/20 text-indigo-300 border border-indigo-500/30"
                    }`}
                  >
                    {isIntraState ? "CGST + SGST APPLICABLE" : "IGST APPLICABLE"}
                  </span>
                </div>

                <div className="space-y-2.5 py-4 text-xs sm:text-sm">
                  <div className="flex justify-between text-slate-300">
                    <span>Base Amount:</span>
                    <span className="font-semibold text-white">
                      ₹{baseAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                    </span>
                  </div>

                  {isIntraState ? (
                    <>
                      <div className="flex justify-between text-slate-300 pl-2 border-l-2 border-blue-500">
                        <span>Central GST (CGST @ {gstRate / 2}%):</span>
                        <span className="font-medium text-blue-300">
                          ₹{cgstAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="flex justify-between text-slate-300 pl-2 border-l-2 border-blue-500">
                        <span>State GST (SGST @ {gstRate / 2}%):</span>
                        <span className="font-medium text-blue-300">
                          ₹{sgstAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between text-slate-300 pl-2 border-l-2 border-indigo-500">
                      <span>Integrated GST (IGST @ {gstRate}%):</span>
                      <span className="font-medium text-indigo-300">
                        ₹{igstAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}

                  {includePlatformFee && (
                    <div className="flex justify-between text-slate-300 pl-2 border-l-2 border-emerald-500">
                      <span>Platform Convenience Fee:</span>
                      <span className="font-medium text-emerald-300">
                        ₹{platformFeeAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}

                  {includeOtherChargesUnderGst && platformFeeAmount > 0 && (
                    <div className="flex justify-between text-xs text-indigo-300 pl-2 border-l-2 border-indigo-400 bg-indigo-950/40 py-1 px-2 rounded">
                      <span>GST Applied On (Taxable Base + Fee):</span>
                      <span className="font-semibold">
                        ₹{taxableAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="pt-3 border-t border-slate-700 flex items-center justify-between">
                <div>
                  <span className="text-xs text-slate-400 block">Total Payable</span>
                  <span className="text-xs text-slate-400">
                    ({isIntraState ? "Base + CGST + SGST + Charges" : "Base + IGST + Charges"})
                  </span>
                </div>
                <div className="text-2xl font-bold text-emerald-400">
                  ₹{grandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* CHARGES & TAX CONFIGURATION TABLE */}
        {/* ========================================================================= */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-visible">
          {/* Table Toolbar */}
          <div className="p-3.5 sm:p-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 sm:gap-3 w-full sm:w-auto">
              <div className="relative w-full sm:w-72">
                <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, code..."
                  className="w-full pl-9 pr-3.5 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full sm:w-auto px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="All">All Categories</option>
                <option value="GST">GST Only</option>
                <option value="Other Charge">Other Charges Only</option>
              </select>
            </div>

            {(searchQuery || categoryFilter !== "All") && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setCategoryFilter("All");
                }}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-1 px-3 py-2 text-xs text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 font-medium cursor-pointer"
              >
                <FaUndo className="text-[10px]" />
                <span>Reset</span>
              </button>
            )}
          </div>

          {/* Table Container - no nested scrollbars and allows floating menus */}
          <div className="w-full overflow-visible no-scrollbar">
            <table className="w-full text-left border-collapse table-auto">
              <thead>
                <tr className="bg-gray-50/70 border-b border-gray-100 text-[11px] font-semibold uppercase tracking-wider text-black">
                  <th className="py-3 px-3 sm:px-4">Charge / Tax Name</th>
                  <th className="py-3 px-2 sm:px-4">Code</th>
                  <th className="py-3 px-2 sm:px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-xs">
                {filteredCharges.length > 0 ? (
                  filteredCharges.map((item, index) => {
                    const isNearBottom = index >= Math.max(0, filteredCharges.length - 4);
                    return (
                      <tr key={item.id} className="hover:bg-gray-50/60 transition-colors">
                        <td className="py-3 sm:py-3.5 px-3 sm:px-4 font-medium text-black">
                          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                            <span className="font-semibold text-black text-xs sm:text-sm">{item.name}</span>
                            {item.isDefault && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-950 border border-amber-400">
                                <FaStar className="text-amber-600 text-[9px]" />
                                <span>Default</span>
                              </span>
                            )}
                            {!item.isActive && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-200 text-black border border-gray-400">
                                Inactive
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] sm:text-[11px] text-gray-800 font-medium mt-0.5">{item.description}</div>
                        </td>

                        <td className="py-3 sm:py-3.5 px-2 sm:px-4">
                          <span className="font-mono bg-gray-100 border border-gray-300 px-1.5 sm:px-2 py-0.5 rounded text-black font-bold text-[10px] sm:text-[11px] whitespace-nowrap">
                            {item.code}
                          </span>
                        </td>

                        <td className="py-3 sm:py-3.5 px-2 sm:px-4 text-right relative">
                          {/* Three Dot Trigger Button */}
                          <button
                            type="button"
                            onClick={() =>
                              setOpenMenuId(openMenuId === item.id ? null : item.id)
                            }
                            className="p-1.5 text-black hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                            title="Actions & Details"
                          >
                            <FaEllipsisV size={13} />
                          </button>

                          {/* Three Dot Dropdown Menu */}
                          {openMenuId === item.id && (
                            <>
                              {/* Backdrop to close when clicking outside */}
                              <div
                                className="fixed inset-0 z-40"
                                onClick={() => setOpenMenuId(null)}
                              />

                              <div
                                className={`absolute right-1 sm:right-4 w-72 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl border border-gray-200 py-2 z-50 animate-fadeIn text-left ${
                                  isNearBottom ? "bottom-full mb-2" : "top-full mt-1"
                                }`}
                              >
                                {/* Quick Info Summary: High Contrast Black Text */}
                                <div className="px-3.5 py-2.5 border-b border-gray-100 bg-gray-50/70 text-xs space-y-1.5 text-black">
                                  <div className="flex items-center justify-between">
                                    <span className="text-black font-bold">Category:</span>
                                    <span
                                      className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                        item.category === "GST"
                                          ? "bg-blue-100 text-blue-950 border border-blue-400"
                                          : "bg-purple-100 text-purple-950 border border-purple-400"
                                      }`}
                                    >
                                      {item.category}
                                    </span>
                                  </div>

                                  <div className="flex items-center justify-between">
                                    <span className="text-black font-bold">Rate / Value:</span>
                                    <span className="font-bold text-black">
                                      {item.type === "Percentage" ? `${item.rate}%` : `₹${item.rate}`}
                                    </span>
                                  </div>

                                  {item.category === "GST" ? (
                                    <div className="pt-1 border-t border-gray-200/60 text-[11px] space-y-0.5 text-black">
                                      <div className="flex justify-between text-black font-medium">
                                        <span className="font-bold">Intra-State:</span>
                                        <span className="font-semibold text-black">CGST ({item.cgstRate}%) + SGST ({item.sgstRate}%)</span>
                                      </div>
                                      <div className="flex justify-between text-black font-medium">
                                        <span className="font-bold">Inter-State:</span>
                                        <span className="font-semibold text-black">IGST ({item.igstRate}%)</span>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex items-center justify-between text-black">
                                      <span className="font-bold">State Rule:</span>
                                      <span className="font-semibold text-black">Flat across states</span>
                                    </div>
                                  )}

                                  <div className="flex items-center justify-between pt-1 border-t border-gray-200/60 text-black">
                                    <span className="text-black font-bold">Scope:</span>
                                    <span className="text-black font-semibold truncate max-w-[150px]">
                                      {item.scope}
                                    </span>
                                  </div>

                                  <div className="flex items-center justify-between pt-1 border-t border-gray-200/60 text-black">
                                    <span className="text-black font-bold">Status:</span>
                                    <span
                                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                        item.isActive
                                          ? "bg-emerald-100 text-emerald-950 border border-emerald-400"
                                          : "bg-gray-200 text-black border border-gray-400"
                                      }`}
                                    >
                                      {item.isActive ? "Active" : "Inactive"}
                                    </span>
                                  </div>
                                </div>

                                {/* Menu Actions */}
                                <div className="py-1">
                                  {/* Set as Default GST (only for GST category) */}
                                  {item.category === "GST" && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        handleSetDefaultGst(item.id);
                                        setOpenMenuId(null);
                                      }}
                                      className="w-full px-3.5 py-2 text-xs font-semibold text-black hover:bg-amber-50 hover:text-amber-900 flex items-center gap-2.5 transition-colors cursor-pointer"
                                    >
                                      {item.isDefault ? (
                                        <FaStar className="text-amber-600 text-xs" />
                                      ) : (
                                        <FaRegStar className="text-amber-600 text-xs" />
                                      )}
                                      <span>
                                        {item.isDefault ? "Currently Default GST" : "Set as Default GST Rate"}
                                      </span>
                                    </button>
                                  )}

                                  {/* Toggle Active / Inactive Status */}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      handleToggleStatus(item.id);
                                      setOpenMenuId(null);
                                    }}
                                    className="w-full px-3.5 py-2 text-xs font-semibold text-black hover:bg-emerald-50 hover:text-emerald-900 flex items-center gap-2.5 transition-colors cursor-pointer"
                                  >
                                    {item.isActive ? (
                                      <>
                                        <FaTimesCircle className="text-gray-600 text-xs" />
                                        <span>Mark as Inactive</span>
                                      </>
                                    ) : (
                                      <>
                                        <FaCheckCircle className="text-emerald-600 text-xs" />
                                        <span>Mark as Active</span>
                                      </>
                                    )}
                                  </button>

                                  {/* View Details Modal */}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setViewingCharge(item);
                                      setOpenMenuId(null);
                                    }}
                                    className="w-full px-3.5 py-2 text-xs font-semibold text-black hover:bg-blue-50 hover:text-blue-900 flex items-center gap-2.5 transition-colors cursor-pointer"
                                  >
                                    <FaEye className="text-black text-xs" />
                                    <span>View Full Details</span>
                                  </button>

                                  {/* Edit Configuration */}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      handleOpenEdit(item);
                                      setOpenMenuId(null);
                                    }}
                                    className="w-full px-3.5 py-2 text-xs font-semibold text-black hover:bg-blue-50 hover:text-blue-900 flex items-center gap-2.5 transition-colors cursor-pointer"
                                  >
                                    <FaEdit className="text-black text-xs" />
                                    <span>Edit Configuration</span>
                                  </button>

                                  {/* Delete Configuration */}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      handleDeleteCharge(item.id);
                                    }}
                                    className="w-full px-3.5 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 flex items-center gap-2.5 transition-colors cursor-pointer"
                                  >
                                    <FaTrash className="text-red-600 text-xs" />
                                    <span>Delete Charge</span>
                                  </button>
                                </div>
                              </div>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="3" className="py-8 text-center text-black font-semibold">
                      No tax or charge configurations found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* ADD / EDIT TAX OR CHARGE MODAL */}
      {/* ========================================================================= */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4 backdrop-blur-xs no-scrollbar">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] flex flex-col shadow-2xl border border-gray-200 overflow-hidden animate-fadeIn">
            {/* Header */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-3.5 sm:py-4 border-b border-gray-200 bg-gray-50/90 shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <FaReceipt className="text-blue-600 shrink-0" />
                <h3 className="font-bold text-black text-base truncate">
                  {editingCharge ? "Edit Tax / Charge" : "Add New Tax / Charge"}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-black hover:text-gray-700 p-1.5 rounded-lg hover:bg-gray-100 cursor-pointer shrink-0"
              >
                <FaTimes size={16} />
              </button>
            </div>

            {/* Form Body */}
            <form onSubmit={handleSaveCharge} className="p-4 sm:p-6 space-y-3.5 sm:space-y-4 text-xs overflow-y-auto flex-1">
              <div>
                <label className="block font-bold text-black uppercase mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Standard GST Slab or Platform Fee"
                  className="w-full px-3.5 py-2 text-sm text-black border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-black uppercase mb-1">
                    Code Identifier *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="e.g. GST_18, PLATFORM_FEE"
                    className="w-full px-3.5 py-2 text-sm text-black border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono uppercase font-bold"
                  />
                </div>

                <div>
                  <label className="block font-bold text-black uppercase mb-1">
                    Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3.5 py-2 text-sm text-black border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-medium"
                  >
                    <option value="GST">GST (CGST, SGST, IGST)</option>
                    <option value="Other Charge">Other Charge (Platform / Audit Fee)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-black uppercase mb-1">
                    Rate Type *
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-3.5 py-2 text-sm text-black border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-medium"
                  >
                    <option value="Percentage">Percentage (%)</option>
                    <option value="Flat">Flat Amount (₹)</option>
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-black uppercase mb-1">
                    Rate / Value *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={formData.rate}
                    onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                    placeholder={formData.type === "Percentage" ? "18" : "20"}
                    className="w-full px-3.5 py-2 text-sm text-black border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-bold"
                  />
                </div>
              </div>

              {formData.category === "GST" && (
                <div className="p-3 bg-blue-50 rounded-lg text-xs text-black border border-blue-200">
                  <span className="font-bold block mb-1">Auto-calculated GST Split:</span>
                  <div className="flex flex-col sm:flex-row justify-between gap-1 font-semibold">
                    <span>Intra-state: CGST ({(parseFloat(formData.rate) || 0) / 2}%) + SGST ({(parseFloat(formData.rate) || 0) / 2}%)</span>
                    <span>Inter-state: IGST ({parseFloat(formData.rate) || 0}%)</span>
                  </div>
                </div>
              )}

              <div>
                <label className="block font-bold text-black uppercase mb-1">
                  Description
                </label>
                <textarea
                  rows={2}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Notes or statutory notes..."
                  className="w-full px-3.5 py-2 text-sm text-black border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                />
              </div>

              <div className="space-y-2 pt-1">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isActiveToggle"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <label htmlFor="isActiveToggle" className="text-xs font-semibold text-black cursor-pointer">
                    Activate this charge immediately
                  </label>
                </div>

                {formData.category === "GST" && (
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="isDefaultToggle"
                      checked={formData.isDefault}
                      onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                      className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                    />
                    <label htmlFor="isDefaultToggle" className="text-xs font-bold text-black cursor-pointer flex items-center gap-1">
                      <FaStar className="text-amber-500 text-[10px]" />
                      <span>Set as system default GST rate (replaces any previous default)</span>
                    </label>
                  </div>
                )}
              </div>

              <div className="pt-4 border-t border-gray-200 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-xs font-bold text-black bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm cursor-pointer"
                >
                  {editingCharge ? "Save Changes" : "Create Charge"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* ========================================================= */}
      {/* VIEW DETAILS MODAL */}
      {/* ========================================================= */}
      {viewingCharge && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4 backdrop-blur-xs no-scrollbar">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] flex flex-col shadow-2xl border border-gray-200 overflow-hidden animate-fadeIn">
            {/* Header */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-3.5 sm:py-4 border-b border-gray-200 bg-gray-50/90 shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <FaReceipt className="text-blue-600 text-lg shrink-0" />
                <h3 className="font-bold text-black text-base truncate">
                  {viewingCharge.name}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setViewingCharge(null)}
                className="text-black hover:text-gray-700 p-1.5 rounded-lg hover:bg-gray-200 cursor-pointer shrink-0"
              >
                <FaTimes size={16} />
              </button>
            </div>

            {/* Content - High Contrast Black Text */}
            <div className="p-4 sm:p-6 space-y-3 sm:space-y-3.5 text-xs text-black overflow-y-auto flex-1">
              <div className="flex items-center justify-between pb-2 border-b border-gray-200">
                <span className="text-black font-bold uppercase tracking-wider text-[11px]">Identifier Code</span>
                <span className="font-mono font-bold text-black bg-gray-100 border border-gray-300 px-2 py-0.5 rounded text-xs">
                  {viewingCharge.code}
                </span>
              </div>

              <div className="flex items-center justify-between pb-2 border-b border-gray-200">
                <span className="text-black font-bold uppercase tracking-wider text-[11px]">Category</span>
                <span
                  className={`px-2.5 py-0.5 rounded-full font-bold text-xs ${
                    viewingCharge.category === "GST"
                      ? "bg-blue-100 text-blue-950 border border-blue-400"
                      : "bg-purple-100 text-purple-950 border border-purple-400"
                  }`}
                >
                  {viewingCharge.category}
                </span>
              </div>

              <div className="flex items-center justify-between pb-2 border-b border-gray-200">
                <span className="text-black font-bold uppercase tracking-wider text-[11px]">Configured Rate</span>
                <span className="font-bold text-base text-black">
                  {viewingCharge.type === "Percentage" ? `${viewingCharge.rate}%` : `₹${viewingCharge.rate}`}
                </span>
              </div>

              {viewingCharge.category === "GST" && (
                <div className="p-3 sm:p-3.5 bg-blue-50/90 rounded-xl border border-blue-200 space-y-1.5">
                  <span className="font-bold text-black block text-xs">State Split Breakdown:</span>
                  <div className="flex flex-col sm:flex-row justify-between text-black text-xs font-medium gap-0.5 sm:gap-0">
                    <span className="font-semibold text-black">Intra-State (Same State):</span>
                    <span className="font-bold text-black">
                      CGST {viewingCharge.cgstRate}% + SGST {viewingCharge.sgstRate}%
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row justify-between text-black text-xs font-medium gap-0.5 sm:gap-0">
                    <span className="font-semibold text-black">Inter-State (Different State):</span>
                    <span className="font-bold text-black">IGST {viewingCharge.igstRate}%</span>
                  </div>
                </div>
              )}

              <div className="flex items-start sm:items-center justify-between pb-2 border-b border-gray-200 gap-2">
                <span className="text-black font-bold uppercase tracking-wider text-[11px] shrink-0">Scope & Applicability</span>
                <span className="font-bold text-black text-xs text-right">
                  {viewingCharge.scope}
                  <div className="text-[11px] text-black font-medium mt-0.5">{viewingCharge.applicableOn}</div>
                </span>
              </div>

              {viewingCharge.category === "GST" && (
                <div className="flex items-center justify-between pb-2 border-b border-gray-200">
                  <span className="text-black font-bold uppercase tracking-wider text-[11px]">Default Status</span>
                  <span>
                    {viewingCharge.isDefault ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-950 border border-amber-400">
                        <FaStar className="text-amber-600 text-xs" />
                        <span>System Default GST</span>
                      </span>
                    ) : (
                      <span className="text-black font-bold text-xs">Standard Slab</span>
                    )}
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between pb-2 border-b border-gray-200">
                <span className="text-black font-bold uppercase tracking-wider text-[11px]">Operating Status</span>
                <span
                  className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold ${
                    viewingCharge.isActive
                      ? "bg-emerald-100 text-emerald-950 border border-emerald-400"
                      : "bg-gray-200 text-black border border-gray-400"
                  }`}
                >
                  {viewingCharge.isActive ? "Active" : "Inactive"}
                </span>
              </div>

              {viewingCharge.description && (
                <div className="pt-1">
                  <span className="text-black font-bold uppercase tracking-wider text-[11px] block mb-1">Description</span>
                  <p className="text-black bg-gray-50 p-2.5 rounded-lg border border-gray-200 font-normal leading-relaxed text-xs">
                    {viewingCharge.description}
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 sm:px-6 py-3 sm:py-3.5 bg-gray-50/90 border-t border-gray-200 flex items-center justify-end shrink-0">
              <button
                type="button"
                onClick={() => setViewingCharge(null)}
                className="px-5 py-2 text-xs font-bold text-black bg-white border border-gray-300 rounded-lg hover:bg-gray-100 shadow-xs cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxManagementPanel;
